import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../design-system/components/composite/Modal/Modal";
import { Button, StatusPill, type StatusVariant } from "../../atoms";
import { DataTable, Pagination, type DataTableColumn } from "../../molecules";
import type { ScheduleRefreshRecord } from "../../../pages/Connectors/api";
import type { ScheduleLifecycleLabelStatus } from "../../../utils/scheduleRefreshFormat";
import {
  deriveScheduleDisplayName,
  formatScheduleHistoryFrequency,
  formatScheduleLifecycleStatus,
  formatScheduleRefreshType,
  formatScheduleTimezoneShort,
  formatScheduleWallTime,
} from "../../../utils/scheduleRefreshFormat";
import { ScheduleRowActionsMenu } from "./ScheduleRowActionsMenu";
import {
  FooterBar,
  HeaderTitle,
  ModalSubtitle,
  TableCard,
} from "./ScheduleHistoryModal.styles";
import type { ScheduleHistoryModalProps } from "./ScheduleHistoryModal.types";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [5, 10, 25] as const;

const STATUS_TO_PILL: Record<ScheduleLifecycleLabelStatus, StatusVariant> = {
  active: "success",
  paused: "warning",
  cancelled: "error",
};

/**
 * Lists refresh schedules for a dataset — layout matches the Schedules history
 * design (columns, status chips, pagination inside the table card).
 */
export const ScheduleHistoryModal: React.FC<ScheduleHistoryModalProps> = ({
  open,
  onClose,
  rows,
  isBusy = false,
  onAddSchedule,
  onEditSchedule,
  onPauseOrResumeSchedule,
  onCancelSchedule,
  onDeleteSchedule,
}) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    if (!open) setOpenMenuId(null);
  }, [open]);

  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, rows.length]);

  const totalCount = rows.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  const pageRows = useMemo(
    () => rows.slice((page - 1) * pageSize, page * pageSize),
    [rows, page, pageSize],
  );

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const columns = useMemo(
    (): Array<DataTableColumn<ScheduleRefreshRecord>> => [
      {
        key: "name",
        header: "SCHEDULE NAME",
        width: "16%",
        bodyVariant: 3,
        emphasis: "high",
        render: (row) =>
          row.name?.trim() || deriveScheduleDisplayName(row.config),
      },
      {
        key: "frequency",
        header: "FREQUENCY",
        width: "28%",
        noWrap: true,
        bodyVariant: 4,
        emphasis: "medium",
        render: (row) => formatScheduleHistoryFrequency(row.config),
      },
      {
        key: "time",
        header: "TIME",
        width: "10%",
        bodyVariant: 4,
        emphasis: "medium",
        render: (row) => formatScheduleWallTime(row.config.refreshHour),
      },
      {
        key: "timezone",
        header: "TIMEZONE",
        width: "9%",
        bodyVariant: 4,
        emphasis: "medium",
        render: (row) => formatScheduleTimezoneShort(row.config.timezone),
      },
      {
        key: "status",
        header: "STATUS",
        width: "12%",
        bodyVariant: 4,
        emphasis: "medium",
        render: (row) => (
          <StatusPill variant={STATUS_TO_PILL[row.status]}>
            {formatScheduleLifecycleStatus(row.status)}
          </StatusPill>
        ),
      },
      {
        key: "type",
        header: "TYPE",
        width: "9%",
        bodyVariant: 4,
        emphasis: "medium",
        render: (row) => formatScheduleRefreshType(row.config),
      },
      {
        key: "actions",
        header: "ACTION",
        width: "48px",
        align: "right",
        bodyVariant: 3,
        emphasis: "interactive",
        render: (row) => (
          <ScheduleRowActionsMenu
            record={row}
            openMenuId={openMenuId}
            onOpen={setOpenMenuId}
            onClose={() => setOpenMenuId(null)}
            onEdit={onEditSchedule}
            onPauseOrResume={onPauseOrResumeSchedule}
            onCancel={onCancelSchedule}
            onDelete={onDeleteSchedule}
          />
        ),
      },
    ],
    [
      openMenuId,
      onEditSchedule,
      onPauseOrResumeSchedule,
      onCancelSchedule,
      onDeleteSchedule,
    ],
  );

  return (
    <Modal open={open} onClose={onClose} size="xl">
      <ModalHeader onClose={onClose}>
        <div>
          <HeaderTitle>Schedules</HeaderTitle>
          <ModalSubtitle>
            View and manage all refresh schedules for this dataset
          </ModalSubtitle>
        </div>
      </ModalHeader>

      <ModalBody>
        <TableCard>
          <DataTable
            chromeless
            tableLayoutFixed
            columns={columns}
            rows={pageRows}
            rowKey={(row) => row.id}
          />
          <Pagination
            page={page}
            pageCount={pageCount}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPage}
            pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
            onPageSizeChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </TableCard>
      </ModalBody>

      <ModalFooter>
        <FooterBar>
          <Button
            variant="link-pill"
            size="md"
            onClick={onClose}
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button
            variant="primary-dark"
            size="md"
            onClick={onAddSchedule}
            disabled={isBusy}
          >
            Add new schedule
          </Button>
        </FooterBar>
      </ModalFooter>
    </Modal>
  );
};

export default ScheduleHistoryModal;
